import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Check whether the shared Google Calendar account is connected (server-side tokens exist).
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const fn = httpsCallable<void, { connected: boolean }>(functions, 'calendarStatus');
    const result = await fn();
    return result.data.connected;
  } catch (err) {
    console.error('Error checking calendar status:', err);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Availability & overlap checking (server-side)
// ---------------------------------------------------------------------------

interface TimeSlot {
  time: string;
  available: boolean;
}

export const getAvailableTimeSlots = async (
  date: Date,
  excludeEventId?: string
): Promise<TimeSlot[]> => {
  try {
    const fn = httpsCallable<
      { dateISO: string; excludeEventId?: string },
      { slots: TimeSlot[] }
    >(functions, 'calendarGetAvailability');
    const result = await fn({ dateISO: date.toISOString(), excludeEventId });
    return result.data.slots;
  } catch (err) {
    console.error('Error getting available time slots:', err);
    return generateAllSlots();
  }
};

interface OverlapResult {
  hasOverlap: boolean;
  overlappingEventDetails: {
    summary: string;
    location?: string;
    startTime: string;
    endTime: string;
  }[];
}

export const checkTimeSlotOverlap = async (
  date: Date,
  startTime: string,
  endTime?: string,
  excludeEventId?: string
): Promise<OverlapResult & { overlappingEvents: any[] }> => {
  try {
    const fn = httpsCallable<
      { dateISO: string; startTime: string; endTime?: string; excludeEventId?: string },
      OverlapResult
    >(functions, 'calendarCheckOverlap');
    const result = await fn({
      dateISO: date.toISOString(),
      startTime,
      endTime,
      excludeEventId
    });
    return { ...result.data, overlappingEvents: result.data.overlappingEventDetails };
  } catch (err) {
    console.error('Error checking time slot overlap:', err);
    return { hasOverlap: false, overlappingEvents: [], overlappingEventDetails: [] };
  }
};

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export const forceSync = async (projectId: string): Promise<boolean> => {
  try {
    const fn = httpsCallable<{ projectId: string }, { success: boolean }>(
      functions, 'calendarForceSync'
    );
    const result = await fn({ projectId });
    return result.data.success;
  } catch (err) {
    console.error('Error force-syncing project:', err);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateAllSlots(): TimeSlot[] {
  return Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;
    return {
      time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      available: true
    };
  });
}
